import {
  createContext,
  createEffect,
  createSignal,
  untrack,
  useContext,
  type Accessor,
  type JSX,
} from "solid-js";
import { useApi, isOk } from "../api";
import { useUserContext } from "../components/UserContextProvider";
import { useFeatureFlag } from "../hooks/useFeatureFlag";

interface MutedContactsState {
  mutedNames: Accessor<readonly string[]>;
  loading: Accessor<boolean>;
  isMuted: (name: string) => boolean;
  mute: (name: string) => Promise<boolean>;
  unmute: (name: string) => Promise<boolean>;
  reload: () => Promise<void>;
}

const MutedContactsContext = createContext<MutedContactsState>();

const norm = (name: string) => name.trim().toLowerCase();

export function MutedContactsProvider(props: { children: JSX.Element }) {
  const api = useApi();
  const { user } = useUserContext();
  const msgsEnabled = useFeatureFlag("MSGS");

  const [names, setNames] = createSignal<readonly string[]>([]);
  const [loading, setLoading] = createSignal(true);

  const reload = async () => {
    if (!user()?.loggedin || !msgsEnabled()) {
      setNames([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.msgContactMute.getMutedContacts();
      if (isOk(res.data)) setNames((res.data.value.contacts ?? []).map((c) => c.name));
    } catch {
      // leave existing list; surface nothing, this is a background load
    } finally {
      setLoading(false);
    }
  };

  const mute = async (name: string) => {
    const key = norm(name);
    if (!key) return false;
    try {
      const res = await api.msgContactMute.muteContact({ name: key });
      if (!isOk(res.data)) return false;
      setNames((res.data.value.contacts ?? []).map((c) => c.name));
      return true;
    } catch {
      return false;
    }
  };

  const unmute = async (name: string) => {
    try {
      const res = await api.msgContactMute.unmuteContact(norm(name));
      if (!isOk(res.data)) return false;
      setNames((res.data.value.contacts ?? []).map((c) => c.name));
      return true;
    } catch {
      return false;
    }
  };

  const isMuted = (name: string) => names().includes(norm(name));

  createEffect(() => {
    const ready = !!user()?.loggedin && msgsEnabled();
    setLoading(true);
    if (ready) untrack(() => void reload());
    else {
      setNames([]);
      setLoading(false);
    }
  });

  const state: MutedContactsState = {
    mutedNames: names,
    loading,
    isMuted,
    mute,
    unmute,
    reload,
  };

  return <MutedContactsContext.Provider value={state}>{props.children}</MutedContactsContext.Provider>;
}

export function useMutedContacts(): MutedContactsState {
  const ctx = useContext(MutedContactsContext);
  if (!ctx) throw new Error("useMutedContacts must be used within MutedContactsProvider");
  return ctx;
}
